<template>
  <SettingsLayout>
    <Suspense>
      <div class="settings-content">
        <div class="heading">
          <h1 class="h2">Billing</h1>
        </div>
        <div class="block">
          <div class="mxw">
            <template
              v-if="
                refreshing ||
                !workspaceStore.subscriptionLoaded ||
                !workspaceStore.billingPortalLoaded
              "
            >
              Loading billing information...
            </template>
            <!-- The status check failed: say so, rather than claiming the user
                 isn't subscribed. -->
            <template v-else-if="!workspaceStore.subscriptionStatusSucceeded">
              <p>
                We couldn't load your subscription details right now. Please
                refresh the page, or contact
                <a href="mailto:support@audioscribe.io"
                  >support@audioscribe.io</a
                >.
              </p>
            </template>
            <template v-else-if="!workspaceStore.isSubscribedState">
              <p>
                It seems that you are not currently subscribed. Please
                <router-link to="/settings/plans">click here</router-link> to
                view our subscription plans and subscribe.
              </p>
            </template>
            <!-- A plan AudioScribe granted: nothing to pay or manage. -->
            <template v-else-if="workspaceStore.subscriptionSource === 'comp'">
              <p>
                Your Pro plan was provided by AudioScribe, so there's nothing to
                pay or manage here.
              </p>
            </template>
            <template v-else>
              <p class="billing-where">{{ whereSubscribed }}</p>

              <!-- Web (Stripe): the billing portal. -->
              <div v-if="providers.includes('stripe')" class="billing-provider">
                <h2 v-if="providers.length > 1" class="h3">On the web</h2>
                <template v-if="workspaceStore.billingPortalUrl">
                  <p>
                    Manage your plan, payment method and invoices in the billing
                    portal:
                  </p>
                  <p>
                    <a
                      :href="workspaceStore.billingPortalUrl"
                      @click="trackBillingPortalClicked"
                      >Billing portal</a
                    >
                  </p>
                </template>
                <p v-else>
                  Billing management is temporarily unavailable — please try
                  again shortly, or contact
                  <a href="mailto:support@audioscribe.io"
                    >support@audioscribe.io</a
                  >.
                </p>
              </div>

              <!-- App Store: managed on-device, not via the Stripe portal. -->
              <div v-if="providers.includes('apple')" class="billing-provider">
                <h2 v-if="providers.length > 1" class="h3">In the App Store</h2>
                <p>
                  To view, change, or cancel it, open the
                  <strong>Settings</strong> app on your iPhone or iPad, tap your
                  name at the top, then tap <strong>Subscriptions</strong> and
                  select AudioScribe. You can also manage it at
                  <a
                    href="https://apps.apple.com/account/subscriptions"
                    target="_blank"
                    rel="noopener"
                    >apps.apple.com/account/subscriptions</a
                  >.
                </p>
              </div>

              <!-- Google Play: managed in the Play Store. -->
              <div v-if="providers.includes('google')" class="billing-provider">
                <h2 v-if="providers.length > 1" class="h3">On Google Play</h2>
                <p>
                  To view, change, or cancel it, open the
                  <strong>Play Store</strong> app on your Android device, tap
                  your profile icon, then tap
                  <strong
                    >Payments &amp; subscriptions &rsaquo; Subscriptions</strong
                  >
                  and select AudioScribe. You can also manage it at
                  <a
                    href="https://play.google.com/store/account/subscriptions"
                    target="_blank"
                    rel="noopener"
                    >play.google.com/store/account/subscriptions</a
                  >
                  (sign in with the Google account you used to subscribe).
                </p>
              </div>
            </template>
          </div>
        </div>
      </div>
      <template #fallback> Loading... </template>
    </Suspense>
  </SettingsLayout>
</template>

<script>
import SettingsLayout from "@/components/SettingsLayout.vue";
import { useWorkSpaceStore } from "@/stores/workspace-store";
import { mapStores } from "pinia";
import { analytics } from "@/services/analytics.service";

// Where each provider's subscription is managed, keyed like the
// entitlement's `sources` ({ stripe, apple, google }).
const WHERE = {
  stripe: "on the web",
  apple: "through the App Store",
  google: "through Google Play",
};

// Fallback when the API doesn't list the providers: infer from `source`.
// "both" can't say which, so every way to manage it is shown.
const PROVIDERS_BY_SOURCE = {
  stripe: ["stripe"],
  apple: ["apple"],
  google_play: ["google"],
  both: ["stripe", "apple", "google"],
};

export default {
  name: "SettingsBillingView",
  components: {
    SettingsLayout,
  },
  computed: {
    ...mapStores(useWorkSpaceStore),
    // The providers that grant the subscription.
    providers() {
      const sources = this.workspaceStore.subscriptionSources;
      if (sources) {
        const listed = Object.keys(WHERE).filter((key) => sources[key]);
        if (listed.length) return listed;
      }
      // Unknown or missing source: fall back to the billing portal, as before.
      return (
        PROVIDERS_BY_SOURCE[this.workspaceStore.subscriptionSource] || [
          "stripe",
        ]
      );
    },
    whereSubscribed() {
      if (this.providers.length === 1) {
        return `You subscribed ${WHERE[this.providers[0]]}.`;
      }
      return "You have subscriptions in more than one place. Manage each one where you bought it:";
    },
  },
  data() {
    return {
      // True while this visit's refresh runs: the store reports "not
      // confirmed" until it finishes, which must not read as a failure.
      refreshing: true,
    };
  },
  methods: {
    trackBillingPortalClicked() {
      const sub = this.workspaceStore.subscription;
      analytics.billingManageSubscriptionClicked({
        current_tier: sub?.currentPlanName || "free",
      });
    },
  },
  mounted() {
    // Track billing page viewed
    const sub = this.workspaceStore.subscription;
    analytics.settingsBillingViewed({
      current_tier: sub?.currentPlanName || "free",
    });
  },
  async beforeMount() {
    // Always refresh: the portal link is a short-lived session, and the
    // subscription may have changed in a mobile app since the app loaded.
    try {
      await this.workspaceStore.fetchSubscriptionStatus();
    } finally {
      this.refreshing = false;
    }
  },
};
</script>
